import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Wind, Mail, Lock, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { PasswordInput, isStrongPassword } from '../components/PasswordInput';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { language } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      toast.error(language === 'fr' ? 'Une erreur est survenue' : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 hero-bg">
      <div className="w-full max-w-md">
        <div className="glass-card rounded-3xl p-8 sm:p-10 shadow-xl animate-scale-in">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary mb-4">
              <Wind className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {language === 'fr' ? 'Mot de passe oublié' : 'Forgot password'}
            </h1>
            <p className="text-gray-600">
              {language === 'fr' 
                ? 'Entrez votre email pour réinitialiser votre mot de passe'
                : 'Enter your email to reset your password'}
            </p>
          </div>

          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  {language === 'fr' ? 'Email' : 'Email'}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className="pl-12 h-12 rounded-xl border-gray-200 bg-white/50 focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all text-lg font-semibold"
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {language === 'fr' ? 'Envoyer le lien' : 'Send reset link'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-gray-600" data-testid="forgot-sent-message">
                {language === 'fr' 
                  ? `Si un compte existe avec ${email}, vous allez recevoir un email avec un lien pour choisir un nouveau mot de passe. Pensez à vérifier vos spams.`
                  : `If an account exists for ${email}, you will receive an email with a link to choose a new password. Check your spam folder.`}
              </p>
              <p className="text-xs text-gray-400">
                {language === 'fr' ? 'Le lien est valable 1 heure.' : 'The link is valid for 1 hour.'}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 text-center">
            <Link 
              to="/login" 
              className="text-primary font-semibold hover:underline"
              data-testid="back-login-link"
            >
              {language === 'fr' ? '← Retour à la connexion' : '← Back to login'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { language } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }
    if (!isStrongPassword(password)) {
      toast.error(language === 'fr' ? 'Le mot de passe doit contenir au moins 8 caractères, dont une lettre et un chiffre.' : 'Password must be at least 8 characters with a letter and a number.');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success(language === 'fr' ? 'Mot de passe réinitialisé !' : 'Password reset successfully!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      const message = error.response?.data?.detail || (language === 'fr' ? 'Token invalide ou expiré' : 'Invalid or expired token');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 hero-bg">
        <div className="glass-card rounded-3xl p-8 text-center">
          <p className="text-red-600 mb-4">
            {language === 'fr' ? 'Token de réinitialisation manquant' : 'Reset token missing'}
          </p>
          <Link to="/forgot-password">
            <Button className="rounded-full">
              {language === 'fr' ? 'Demander un nouveau lien' : 'Request a new link'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 hero-bg">
      <div className="w-full max-w-md">
        <div className="glass-card rounded-3xl p-8 sm:p-10 shadow-xl animate-scale-in">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
            </h1>
            <p className="text-gray-600">
              {language === 'fr' 
                ? 'Entrez votre nouveau mot de passe'
                : 'Enter your new password'}
            </p>
          </div>

          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-medium">
                  {language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
                </Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  testId="reset-password-input"
                />
                <p className="text-xs text-gray-500">
                  {language === 'fr' ? '8 caractères minimum, avec au moins une lettre et un chiffre' : 'At least 8 characters, with a letter and a number'}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">
                  {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  testId="reset-confirm-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all text-lg font-semibold"
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-gray-600">
                {language === 'fr' 
                  ? 'Mot de passe réinitialisé ! Redirection...'
                  : 'Password reset! Redirecting...'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
